/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { ColumnFiltersState } from '@tanstack/react-table'
import { DATE_FORMATS } from 'constants/DateFormats'
import { DateRange } from 'types/entities/DateRange'
import { ReservationStatus } from 'types/enums/ReservationStatus'
import { parseFilterDate } from 'utils/dateRangePresets'

export interface ReservationFilterEntry {
    id: string
    value: unknown
}

/** Default reservations table sort: event date descending (furthest future first). */
export const DEFAULT_RESERVATIONS_TABLE_SORTING = [{ id: 'date', desc: true }]

/** Maps the status column filter to the tab view: one value → that status, else All. */
export const deriveReservationStatusFromFilter = (
    columnFilters: ColumnFiltersState
): ReservationStatus => {
    const statusFilter = columnFilters.find((filter) => filter.id === 'status')
    const value = statusFilter?.value

    if (Array.isArray(value) && value.length === 1 && value[0] != null && value[0] !== '') {
        return value[0] as ReservationStatus
    }

    if (typeof value === 'string' && value.length > 0) {
        return value as ReservationStatus
    }

    return ReservationStatus.All
}

const asFilterList = (
    filters: ReservationFilterEntry[] | ReservationFilterEntry | undefined
) => (Array.isArray(filters) ? filters : filters ? [filters] : [])

const toNullableString = (value: unknown): string | null => {
    if (value === undefined || value === null) {
        return null
    }

    const normalized = String(value).trim()
    return normalized.length > 0 ? normalized : null
}

export const mapReservationColumnId = (id: string): string => {
    if (id === 'package') return 'category'
    if (id === 'total') return 'price'
    if (id === 'company_type') return 'company.type'
    if (id === 'company_id') return 'company.name'
    return id
}

export const mapReservationColumnIdForUi = (id: string): string => {
    if (id === 'category') return 'package'
    if (id === 'price') return 'total'
    if (id === 'company.type') return 'company_type'
    if (id === 'company.name') return 'company_id'
    return id
}

export const extractTimeRange = (
    value: unknown
): { from: string | null; to: string | null } | null => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null
    }

    const raw = value as {
        from?: string
        to?: string
        start?: string
        end?: string
    }

    const from = toNullableString(raw.from ?? raw.start)
    const to = toNullableString(raw.to ?? raw.end)

    if (!from && !to) {
        return null
    }

    return { from, to }
}

export const extractNumberRange = (
    value: unknown,
    parser: (value: string) => number
): { from: number | null; to: number | null } | null => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null
    }

    const raw = value as {
        from?: unknown
        to?: unknown
        start?: unknown
        end?: unknown
        min?: unknown
        max?: unknown
    }

    const rawFrom = toNullableString(raw.from ?? raw.start ?? raw.min)
    const rawTo = toNullableString(raw.to ?? raw.end ?? raw.max)

    const fromValue = rawFrom !== null ? parser(rawFrom) : NaN
    const toValue = rawTo !== null ? parser(rawTo) : NaN
    const from = Number.isFinite(fromValue) ? fromValue : null
    const to = Number.isFinite(toValue) ? toValue : null

    if (from === null && to === null) {
        return null
    }

    return { from, to }
}

export const transformReservationSearch = (
    filters: ReservationFilterEntry[] | ReservationFilterEntry | undefined
): ReservationFilterEntry[] => {
    const list = asFilterList(filters)
    const transformed: ReservationFilterEntry[] = []

    for (const filterItem of list) {
        if (!filterItem?.id) continue

        const mappedId = mapReservationColumnId(filterItem.id)
        const value = filterItem.value

        if (mappedId === 'seller_id' && Array.isArray(value)) {
            if (value.length === 1) {
                transformed.push({ id: mappedId, value: value[0] })
                continue
            }

            if (value.length > 1) {
                transformed.push({ id: mappedId, value: value })
                continue
            }
        }

        if (
            (mappedId === 'created_at' || mappedId === 'updated_at' || mappedId === 'date') &&
            value &&
            typeof value === 'object' &&
            !Array.isArray(value) &&
            'from' in value &&
            'to' in value
        ) {
            const from = parseFilterDate((value as DateRange).from)
            const to = parseFilterDate((value as DateRange).to)
            if (!from || !to) {
                continue
            }
            transformed.push({
                id: mappedId,
                value: [
                    mappedId === 'date' ? from.format(DATE_FORMATS.DATE_YMD) : from.startOf('day').format('YYYY-MM-DD HH:mm:ss.SSS'),
                    mappedId === 'date' ? to.format(DATE_FORMATS.DATE_YMD) : to.endOf('day').format('YYYY-MM-DD HH:mm:ss.SSS'),
                ],
            })
            continue
        }

        if (mappedId === 'time') {
            const normalizedTimeRange = extractTimeRange(value)
            if (!normalizedTimeRange) {
                continue
            }
            transformed.push({
                id: mappedId,
                value: normalizedTimeRange,
            })
            continue
        }

        if (mappedId === 'persons') {
            const normalizedRange = extractNumberRange(value, (item) => parseInt(item, 10))
            if (!normalizedRange) {
                continue
            }
            transformed.push({
                id: mappedId,
                value: normalizedRange,
            })
            continue
        }

        if (mappedId === 'price' || mappedId === 'total_invoice') {
            const normalizedRange = extractNumberRange(value, (item) => parseFloat(item))
            if (!normalizedRange) {
                continue
            }
            transformed.push({
                id: mappedId,
                value: normalizedRange,
            })
            continue
        }

        transformed.push({ id: mappedId, value })
    }

    return transformed
}

/** Preserve user filters while enforcing the scope of the online reservations table. */
export const transformOnlineReservationSearch = (
    filters: ReservationFilterEntry[] | ReservationFilterEntry | undefined
): ReservationFilterEntry[] => [
    ...transformReservationSearch(filters).filter((entry) => entry.id !== 'status'),
    {id: 'status', value: [ReservationStatus.Reserved]},
]
